// Screenshot import with a review step: pick the screen type, drop a screenshot,
// eyeball what the model extracted (old -> new per row), untick anything wrong,
// then apply. Nothing touches saved state until Apply.
import { useState } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { cardById, labById, uwById, workshopById } from "../../game/data";
import {
  importScreenshot,
  type ImportKind,
  type ImportMatch,
} from "../../game/importScreenshot";
import type { PlayerState } from "../../game/playerState";

const KINDS: { id: ImportKind; label: string }[] = [
  { id: "workshop", label: "Workshop" },
  { id: "uws", label: "UWs" },
  { id: "cards", label: "Cards" },
  { id: "labs", label: "Labs" },
];

interface Props {
  state: PlayerState;
  update: (mutate: (draft: PlayerState) => void) => void;
}

function nameOf(kind: ImportKind, id: string): string {
  if (kind === "workshop") return workshopById.get(id)?.name ?? id;
  if (kind === "cards") return cardById.get(id)?.name ?? id;
  if (kind === "labs") return labById.get(id)?.name ?? id;
  const [uwId, subId] = id.split("/");
  const uw = uwById.get(uwId);
  return uw ? `${uw.name} ${uw.subStats.find((s) => s.id === subId)?.name ?? subId}` : id;
}

function currentLevel(kind: ImportKind, state: PlayerState, id: string): number {
  if (kind === "workshop") return state.workshop[id] ?? 0;
  if (kind === "cards") return state.cards[id] ?? 0;
  if (kind === "labs") return state.labs[id] ?? 0;
  const [uwId, subId] = id.split("/");
  return state.uws[uwId]?.subStats[subId] ?? 0;
}

function applyMatch(kind: ImportKind, draft: PlayerState, m: ImportMatch): void {
  if (kind === "workshop") draft.workshop[m.id] = m.level;
  else if (kind === "cards") draft.cards[m.id] = m.level;
  else if (kind === "labs") draft.labs[m.id] = m.level;
  else {
    const [uwId, subId] = m.id.split("/");
    const uw = draft.uws[uwId] ?? { unlocked: false, subStats: {}, plusLevel: 0 };
    uw.unlocked = true;
    uw.subStats[subId] = m.level;
    draft.uws[uwId] = uw;
  }
}

export function ImportPanel({ state, update }: Props) {
  const { idToken } = useAuth();
  const [kind, setKind] = useState<ImportKind>("workshop");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [matches, setMatches] = useState<ImportMatch[] | null>(null);
  const [ticked, setTicked] = useState<Set<string>>(new Set());

  const run = async (file: File) => {
    if (!idToken) return;
    setBusy(true);
    setError(null);
    setMatches(null);
    try {
      const found = await importScreenshot(idToken, file, kind);
      setMatches(found);
      setTicked(new Set(found.map((m) => m.id)));
      if (found.length === 0) setError("Nothing recognised — is this the right screen type?");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const apply = () => {
    if (!matches) return;
    update((draft) => {
      for (const m of matches) if (ticked.has(m.id)) applyMatch(kind, draft, m);
    });
    setMatches(null);
  };

  if (!idToken) {
    return (
      <div className="panel">
        <p className="hint">Sign in to import stats from screenshots.</p>
      </div>
    );
  }

  return (
    <div className="panel">
      <div className="subtab-bar cat-utility">
        {KINDS.map((k) => (
          <button
            key={k.id}
            className={k.id === kind ? "active" : ""}
            onClick={() => {
              setKind(k.id);
              setMatches(null);
              setError(null);
            }}
          >
            {k.label}
          </button>
        ))}
      </div>

      <label className={`dropzone${busy ? " busy" : ""}`}>
        <input
          type="file"
          accept="image/*"
          disabled={busy}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void run(f);
            e.target.value = "";
          }}
        />
        {busy ? "Reading screenshot…" : `Drop or choose a ${kind} screenshot`}
      </label>
      <p className="hint">
        Screenshots are downscaled in your browser before upload and read by a small
        vision model (~$0.0002 each, 100/day cap). Review before applying.
      </p>

      {error && <p className="error">{error}</p>}

      {matches && matches.length > 0 && (
        <div className="import-review">
          <div className="import-review-head">
            <span>
              {ticked.size}/{matches.length} to apply
            </span>
            <button className="apply-btn" disabled={ticked.size === 0} onClick={apply}>
              Apply
            </button>
          </div>
          <div className="rows">
            {matches.map((m) => {
              const from = currentLevel(kind, state, m.id);
              const on = ticked.has(m.id);
              return (
                <label key={m.id} className={`import-row${on ? "" : " skipped"}`}>
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() =>
                      setTicked((prev) => {
                        const next = new Set(prev);
                        if (on) next.delete(m.id);
                        else next.add(m.id);
                        return next;
                      })
                    }
                  />
                  <span className="import-row-name">{nameOf(kind, m.id)}</span>
                  <span className="import-row-change">
                    {from} → <strong>{m.level}</strong>
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
